import { apiClient } from "./ApiClient";

// ─────────────────────────────────────────────────────────────────────────────
// Invoice API Service
//
// All calls go through apiClient (Axios instance with auth headers).
// Base path: /invoices
// ─────────────────────────────────────────────────────────────────────────────

/**
 * GET /invoices
 * Fetches all active invoices (newest first).
 */
export const getAllInvoicesApi = () =>
  apiClient.get("/invoices");

/**
 * GET /invoices/{id}
 * Fetches a single invoice by its database ID.
 */
export const getInvoiceByIdApi = (id) =>
  apiClient.get(`/invoices/${id}`);

/**
 * GET /invoices/by-stakeholder/{stakeholderId}
 * Fetches all invoices belonging to one stakeholder.
 */
export const getInvoicesByStakeholderApi = (stakeholderId) =>
  apiClient.get(`/invoices/by-stakeholder/${stakeholderId}`);

/**
 * POST /invoices/add
 * Creates a new invoice and automatically posts the matching ledger entry
 * to the stakeholder's account. Both happen atomically on the backend.
 *
 * Required fields in dto:
 *   invoiceNumber  – unique invoice reference (e.g. "INV-2024-001")
 *   invoiceType    – "SALES" (we issue to customer) or "PURCHASE" (from vendor)
 *   stakeholderId  – ID of the stakeholder
 *   invoiceDate    – ISO date string (YYYY-MM-DD)
 *   amount         – numeric invoice amount
 *   currency       – e.g. "USD", "EUR"
 *
 * Optional fields:
 *   paymentDueDate               – ISO date string (YYYY-MM-DD)
 *   fxRateToUsd                   – defaults to 1 if not provided
 *   fxRateDate                    – defaults to invoiceDate on backend
 *   description                   – short label shown on invoice
 *   notes                         – internal remarks
 *   relatedOrderId                – link to an Order (if applicable)
 *   relatedShippingInstructionId  – link to an SI (if applicable)
 */
export const createInvoiceApi = (dto) =>
  apiClient.post("/invoices/add", dto);

/**
 * POST /invoices/filter
 * Filtered search. All filter fields are optional.
 *
 * Filter fields supported:
 *   invoiceNumber   – partial text match
 *   invoiceType     – "SALES" or "PURCHASE"
 *   stakeholderId   – exact stakeholder ID
 *   status          – "CONFIRMED" or "CANCELLED"
 *   invoiceDateFrom – earliest date (inclusive)
 *   invoiceDateTo   – latest date (inclusive)
 */
export const filterInvoicesApi = (filterDto) =>
  apiClient.post("/invoices/filter", filterDto);

/**
 * GET /invoices/vat-analysis
 * Returns VAT aggregates in HUF.
 * params:
 *   mode: "MONTHLY" | "YEARLY"
 *   year: optional year filter (recommended for MONTHLY)
 */
export const getInvoiceVatAnalysisApi = ({ mode = "MONTHLY", year } = {}) =>
  apiClient.get("/invoices/vat-analysis", {
    params: {
      mode,
      ...(year != null ? { year } : {}),
    },
  });

/**
 * GET /invoices/vat-analysis/details
 * Returns invoice-level VAT lines for a selected year/month period.
 * params:
 *   year: required number
 *   month: optional number 1..12
 */
export const getInvoiceVatAnalysisDetailsApi = ({ year, month } = {}) =>
  apiClient.get("/invoices/vat-analysis/details", {
    params: {
      year,
      ...(month != null ? { month } : {}),
    },
  });

/**
 * PUT /invoices/{id}/cancel
 * Cancels a CONFIRMED invoice and reverses its account entry.
 * Returns the updated invoice with status="CANCELLED".
 */
export const cancelInvoiceApi = (id) =>
  apiClient.put(`/invoices/${id}/cancel`);
